// @ts-ignore
import findCycle from "find-cycle/directed";
import {
  action,
  entries,
  IObservableValue,
  reaction,
  runInAction,
  untracked,
  values,
} from "mobx";
import { useEffect, useState } from "react";
import { DEFAULT_CANVAS_OFFSET, GRID_UNIT } from "../Constants";
import {
  Action,
  ActionType,
  ActivePaneState,
  CanvasLayoutPane,
  CanvasLayoutPaneRenderType,
  EvalPaneInputId,
  LLMConfig,
  Pane,
  PaneFlag,
  PaneInput,
  PaneInputGlobalKey,
  PaneOutput,
  PaneType,
  PaneViewMode,
  Position,
  Rect,
  UndoDeletePaneOutput,
} from "../Types";
import {
  doRectsOverlap,
  hasFlag,
  isPointInsideRect,
  isPresenceAction,
} from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canvasAtom,
  canvasTransformComputed,
  canvasViewport,
  canvasViewportShouldAnimateAtom,
  headerHeightComputed,
  selectedToolAtom,
  undoDeletePaneStackAtom,
  windowViewportAtom,
} from "./CanvasState";
import { paneMutator } from "./PaneMutator";
import {
  RENDER_RUN_BUTTON_HEIGHT,
  RENDER_RUN_BUTTON_WIDTH,
} from "./SandboxConstants";

export const EDIT_LAYOUT_CLASSNAME = "edit-layout";
export const TOPBAR_CLASSNAME = "topbar";
export const TEMPLATE_CANVAS_CLASSNAME = "template-canvas";
export const PANE_CLASSNAME = "pane";
export const PANE_BODY_CLASSNAME = "pane-body";
export const OVERLAY_PANE_CLASSNAME = "overlay-pane";
export const OVERLAY_PANE_HEADER_CLASSNAME =
  "bg-neutral-600 text-neutral-0 dark:bg-neutral-200 dark:text-neutral-600";
export const OVERLAY_PANE_HEADER_BUTTON_CLASSNAME =
  "flex items-center justify-center h-3 w-3 rounded-md bg-neutral-500 text-neutral-0 hover:bg-primary-400 dark:bg-neutral-300 dark:text-neutral-600 dark:hover:bg-neutral-400 dark:hover:text-neutral-700 transition-colors";
export const WAITING_CLASSNAME = "bg-neutral-50";

export function getWidthForMonospaceText(text: string): number {
  return text.length * 7.2;
}

export function getPaneInputRect(
  paneRect: Rect,
  paneInputName: string | undefined,
  inputIndex: number
): Rect {
  const inputWidth =
    8 + getWidthForMonospaceText(paneInputName ?? `${inputIndex}`);
  return [
    paneRect[0] - 3 - inputWidth,
    paneRect[1] + inputIndex * 16 + 8,
    inputWidth,
    16,
  ];
}

export function addPositions(
  p1: Position,
  p2: Position,
  p3?: Position
): Position {
  const rv: Position = [p1[0] + p2[0], p1[1] + p2[1]];
  if (p3 !== undefined) {
    return [rv[0] + p3[0], rv[1] + p3[1]];
  }
  return rv;
}
export function scalePosition(p: Position, scale: number): Position {
  return [p[0] * scale, p[1] * scale];
}
export function positionDistance(p1: Position, p2: Position): number {
  return Math.sqrt(Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2));
}
export function roundPosition(
  p: Position,
  roundFunction = Math.floor
): Position {
  return [roundFunction(p[0]), roundFunction(p[1])];
}
export function roundPositionToGrid(
  p: Position,
  zoom: number,
  roundFunction = Math.floor
): Position {
  const gridSize = GRID_UNIT * zoom;
  return [
    roundFunction(p[0] / gridSize) * gridSize,
    roundFunction(p[1] / gridSize) * gridSize,
  ];
}
export function mapClientPositionToCanvasPosition(clientPosition: Position) {
  const canvasTransform = canvasTransformComputed.get();
  return scalePosition(
    [
      clientPosition[0] - canvasTransform[0],
      clientPosition[1] - canvasTransform[1],
    ],
    1 / canvasViewport.zoom
  );
}
export function loadScript(scriptSrc: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const scriptElement = document.createElement("script");
    scriptElement.onload = () => resolve();
    scriptElement.onerror = () => reject();
    scriptElement.src = scriptSrc;
    document.head.appendChild(scriptElement);
  });
}

export function getPaneIdFromPaneInput(
  paneInput: {
    id?: string;
    source?: [paneId: string, outputIndex: number];
  },
  templateInputSources?: Record<string, [paneId: string, outputIndex: number]>
): string | undefined {
  if (
    templateInputSources !== undefined &&
    paneInput.id !== undefined &&
    templateInputSources[paneInput.id] !== undefined
  ) {
    return templateInputSources[paneInput.id][0];
  }
  const { source: inputPaneSource } = paneInput;
  if (inputPaneSource === undefined) {
    return undefined;
  }
  return inputPaneSource[0];
}

const LAYOUT_ID_CHARS = "0123456789abcdefghijklmnopqrstuvwxyz";
export function getNewLayoutId(): string {
  return new Array(8)
    .fill(undefined)
    .map(
      () => LAYOUT_ID_CHARS[Math.floor(Math.random() * LAYOUT_ID_CHARS.length)]
    )
    .join("");
}

export function convertViewportPositionToCanvasOffset(
  viewportPosition: Position,
  canvasTransform: Position,
  canvasZoom: number
): Position {
  return roundPosition(
    scalePosition(
      [
        viewportPosition[0] - canvasTransform[0],
        viewportPosition[1] - canvasTransform[1],
      ],
      1 / canvasZoom
    )
  );
}

export function convertCanvasPositionToViewportPosition(
  position: Position,
  canvasOffset: Position,
  canvasZoom: number
): Position {
  const scaledPosition = scalePosition(position, canvasZoom);
  return [
    scaledPosition[0] + canvasOffset[0],
    scaledPosition[1] + canvasOffset[1],
  ];
}

const VARIABLE_NAMES_DENYLIST = [
  "inputs",
  "arguments",
  "break",
  "case",
  "catch",
  "class",
  "const",
  "continue",
  "debugger",
  "default",
  "delete",
  "do",
  "else",
  "export",
  "extends",
  "finally",
  "for",
  "function",
  "if",
  "import",
  "in",
  "instanceof",
  "new",
  "return",
  "super",
  "switch",
  "this",
  "throw",
  "try",
  "typeof",
  "var",
  "void",
  "while",
  "with",
  "yield",
];
export const VALID_VARIABLE_NAME_REGEX = /^[a-zA-Z_$][0-9a-zA-Z_$]*$/;
export function validateVariableName(name: string): boolean {
  if (name === "") {
    return false;
  }
  if (VARIABLE_NAMES_DENYLIST.includes(name)) {
    return false;
  }
  return VALID_VARIABLE_NAME_REGEX.test(name);
}

export type NpmPackage = {
  scope: string | undefined;
  name: string;
  version: string | undefined;
  export: string | undefined;
};

export function parseNpmPackage(input: string): NpmPackage {
  let v = input;
  let scope: string | undefined;
  if (v.startsWith("@")) {
    const firstSlashIndex = v.indexOf("/");
    scope = v.slice(0, firstSlashIndex);
    v = v.slice(firstSlashIndex + 1);
  }
  let exportName: string | undefined;
  const slashIndex = v.indexOf("/");
  if (slashIndex !== -1) {
    exportName = v.slice(slashIndex + 1);
    v = v.slice(0, slashIndex);
  }
  const [name, version] = v.split("@");
  return {
    scope,
    name,
    version,
    export: exportName,
  };
}

export function isEmptyEvalExpression(expression: string) {
  return expression.split("\n").every((line) => {
    const trimmed = line.trim();
    return trimmed === "" || trimmed.startsWith("//");
  });
}

export function calculateNewZoomCanvasOffset(
  prevOffset: Position,
  targetViewport: Position,
  prevZoom: number,
  newZoom: number
): Position {
  const adjustedTarget = addPositions(targetViewport, [
    0,
    -headerHeightComputed.get(),
  ]);
  const targetOffset = addPositions(
    scalePosition(adjustedTarget, 1 / prevZoom),
    prevOffset
  );
  return addPositions(
    targetOffset,
    scalePosition(adjustedTarget, -1 / newZoom)
  );
}

export function calculateNewZoomCanvasTransform(
  prevTransform: Position,
  targetViewport: Position,
  prevZoom: number,
  newZoom: number
): Position {
  const targetPosition = [
    (targetViewport[0] - prevTransform[0]) / prevZoom,
    (targetViewport[1] - prevTransform[1]) / prevZoom,
  ];
  return [
    targetViewport[0] - targetPosition[0] * newZoom,
    targetViewport[1] - targetPosition[1] * newZoom,
  ];
}

export function getViewportSize(
  canvasZoom: number,
  headerHeight: number
): Position {
  return [
    windowViewportAtom.value[0] / canvasZoom,
    (windowViewportAtom.value[1] - headerHeight) / canvasZoom,
  ];
}

export function getMinPaneWidth(paneType: PaneType | undefined) {
  switch (paneType) {
    case PaneType.InputText:
    case PaneType.State:
      return 192;
    default:
      return 224;
  }
}

export function resetCanvasOffset() {
  runInAction(() => {
    canvasViewport.offset = [...DEFAULT_CANVAS_OFFSET];
  });
}

// If we have multiple local actions of the same type, we might be able to
// ignore the earlier ones.
export function doesLocalActionReplace(newAction: Action, prevAction: Action) {
  if (
    newAction[0] === ActionType.SetPaneRect &&
    (prevAction[0] === ActionType.SetPaneRect ||
      prevAction[0] === ActionType.SetPanePosition)
  ) {
    return newAction[1].paneId === prevAction[1].paneId;
  }
  if (
    newAction[0] === ActionType.SetPanePosition &&
    prevAction[0] === ActionType.SetPanePosition
  ) {
    return newAction[1].paneId === prevAction[1].paneId;
  }
  // TODO: handle SetPaneRectsMulti, SetLayoutPaneRect, SetLayoutPaneRectsMulti
  if (
    newAction[0] === ActionType.PresenceTextRange &&
    prevAction[0] === ActionType.PresenceTextRange
  ) {
    return newAction[1][0] === prevAction[1][0];
  }
  return isPresenceAction(newAction) && newAction[0] === prevAction[0];
}

function willContainPaneInputCycle(
  panes: Pane[],
  paneId: string,
  inputPaneId: string
) {
  const edges: Record<string, string[]> = {};
  for (const pane of panes) {
    if ("inputs" in pane) {
      edges[pane.id] = pane.inputs
        .map((paneInput) => paneInput.source?.[0])
        .filter((paneId): paneId is string => paneId !== undefined);
    }
  }
  if (edges[paneId] === undefined) {
    edges[paneId] = [];
  }
  edges[paneId].push(inputPaneId);
  return findCycle([paneId], (paneId: string) => edges[paneId]) !== null;
}

export function checkAndConfirmPushPaneInput(
  panes: Pane[],
  paneId: string,
  inputPaneId: string
): boolean {
  if (willContainPaneInputCycle(panes, paneId, inputPaneId)) {
    return window.confirm(
      "This will add a circular dependency and likely freeze your canvas! Do you want to continue?"
    );
  }
  return true;
}

export function statePaneSetterValue(
  outputBox: IObservableValue<PaneOutput>
): PaneOutput {
  return [
    "value",
    action((value: any) => {
      if (typeof value === "function") {
        try {
          const newValue = value(
            outputBox.get()[0] === "value" ? outputBox.get()[1] : undefined
          );
          const newOutput: PaneOutput = ["value", newValue];
          outputBox.set(newOutput);
        } catch {}
      } else {
        const output: PaneOutput = ["value", value];
        outputBox.set(output);
      }
    }),
  ];
}

export function statePaneGetterValue(
  outputBox: IObservableValue<PaneOutput>
): PaneOutput {
  return [
    "value",
    () =>
      untracked(() => {
        if (outputBox.get()[0] !== "value") {
          throw new Error("state has no value");
        }
        return outputBox.get()[1];
      }),
  ];
}

function createUndoDeletionForPaneId(paneId: string) {
  const deletedPane = canvasAtom.value.panes.find((p) => p.id === paneId);
  if (deletedPane === undefined) {
    return undefined;
  }
  const outputs = canvasAtom.value.panes
    .map((p) => {
      const outputs: UndoDeletePaneOutput[] = [];
      if ("inputs" in p) {
        outputs.push(
          ...p.inputs
            .filter((input) => input.source?.[0] === paneId)
            .map((input) => ({
              paneId: p.id,
              paneInputId: input.id,
              outputIndex: input.source![1],
            }))
        );
      }
      if ("renderOutput" in p) {
        if (
          p.renderOutput?.[0] === "custom" &&
          p.renderOutput[1]?.[0] === paneId
        ) {
          outputs.push({
            paneId: p.id,
            renderOutput: ["custom"],
            outputIndex: p.renderOutput[1][1],
          });
        }
        if (
          p.renderOutput?.[0] === "react" &&
          p.renderOutput[1]?.ReactDOM?.[0] === paneId
        ) {
          outputs.push({
            paneId: p.id,
            renderOutput: ["react-dom"],
            outputIndex: p.renderOutput[1].ReactDOM[1],
          });
        }
      }
      if ("inputSource" in p) {
        if (p.inputSource?.[0] === paneId) {
          outputs.push({
            paneId: p.id,
            llmInput: true,
            outputIndex: p.inputSource[1],
          });
        }
      }
      if ("templateInputSources" in p) {
        outputs.push(
          ...Object.entries(p.templateInputSources)
            .filter(([inputId, [inputPaneId]]) => inputPaneId === paneId)
            .map(([inputId, [inputPaneId, outputIndex]]) => ({
              paneId: p.id,
              templateInputSource: inputId,
              outputIndex,
            }))
        );
      }
      return outputs;
    })
    .flat();
  return {
    pane: deletedPane,
    outputs,
    layouts: Object.fromEntries(
      canvasAtom.value.layouts
        .map((layout) => {
          const layoutPane = layout.panes.find((p) => p.id === paneId);
          return layoutPane !== undefined ? [[layout.id, layoutPane]] : [];
        })
        .flat()
    ),
  };
}

export function addUndoDeletionToStack(paneIds: string[]) {
  const deletions = paneIds.flatMap((paneId) => {
    const deletion = createUndoDeletionForPaneId(paneId);
    return deletion !== undefined ? [deletion] : [];
  });
  if (deletions.length > 0) {
    const undoDeleteAction: Action = [
      ActionType.UndoDeletePanes,
      { deletions },
    ];
    runInAction(() => {
      undoDeletePaneStackAtom.value = [
        ...undoDeletePaneStackAtom.value,
        undoDeleteAction,
      ];
    });
  }
}

// This is used for react-virtual. This approach of using activePaneState.rect
// allows us to avoid querying the DOM for dimensions. This will overestimate
// the dimensions, especially if editorHeight is undefined.
export function usePaneOutputDimensions(activePaneState: ActivePaneState): {
  width: number;
  height: number;
} {
  const [dimensions, setDimensions] = useState(() =>
    untracked(() => ({
      width: activePaneState.rect.get()[2],
      height:
        activePaneState.rect.get()[3] -
        ("editorHeight" in activePaneState
          ? activePaneState.editorHeight.get() ?? 0
          : 0),
    }))
  );
  useEffect(() =>
    reaction(
      () => [
        activePaneState.rect.get()[2],
        activePaneState.rect.get()[3],
        "editorHeight" in activePaneState
          ? activePaneState.editorHeight.get() ?? 0
          : 0,
      ],
      ([paneWidth, paneHeight, editorHeight]) => {
        setDimensions({
          width: paneWidth,
          height: paneHeight - (editorHeight ?? 0),
        });
      },
      {
        // This is technically not needed but react-virtual relies on a rerender
        // to rerun useVirtual when parentRef.current is set.
        fireImmediately: true,
      }
    )
  );
  return dimensions;
}

export function hasFocusGuardOpen() {
  return (
    document.body.querySelector("body > [data-radix-focus-guard]") !== null
  );
}

export function getInputPaneIds(pane: Pane): string[] {
  const inputPaneIds = new Set<string>();
  if ("inputs" in pane) {
    pane.inputs.forEach((paneInput) => {
      const inputPaneId = getPaneIdFromPaneInput(paneInput);
      if (inputPaneId !== undefined) {
        inputPaneIds.add(inputPaneId);
      }
    });
  }
  if ("templateInputSources" in pane) {
    Object.values(pane.templateInputSources).forEach(([paneId]) => {
      inputPaneIds.add(paneId);
    });
  }
  if ("renderOutput" in pane) {
    if (
      pane.renderOutput?.[0] === "custom" &&
      pane.renderOutput[1]?.[0] !== undefined
    ) {
      inputPaneIds.add(pane.renderOutput[1][0]);
    }
    if (
      pane.renderOutput?.[0] === "react" &&
      pane.renderOutput[1]?.ReactDOM?.[0] !== undefined
    ) {
      inputPaneIds.add(pane.renderOutput[1].ReactDOM[0]);
    }
  }
  return Array.from(inputPaneIds);
}

export function hasPaneInputMatchingPredicate(
  pane: Pane,
  predicate: (checkPaneId: string) => boolean
) {
  function predicateWithUndefined(checkPaneId: string | undefined) {
    return checkPaneId !== undefined && predicate(checkPaneId);
  }
  if (
    "inputs" in pane &&
    pane.inputs.findIndex((paneInput) =>
      predicateWithUndefined(getPaneIdFromPaneInput(paneInput))
    ) !== -1
  ) {
    return true;
  }
  if (
    "templateInputSources" in pane &&
    Object.values(pane.templateInputSources).some(([paneId]) =>
      predicate(paneId)
    )
  ) {
    return true;
  }
  if (
    "renderOutput" in pane &&
    ((pane.renderOutput?.[0] === "custom" &&
      predicateWithUndefined(pane.renderOutput[1]?.[0])) ||
      (pane.renderOutput?.[0] === "react" &&
        predicateWithUndefined(pane.renderOutput[1]?.ReactDOM?.[0])))
  ) {
    return true;
  }
  return false;
}

export function validatePaneInputName(
  name: string,
  existingPaneInputs: PaneInput[],
  paneInputId: string | undefined
) {
  if (!validateVariableName(name)) {
    name = `${name}_`;
  }
  while (
    existingPaneInputs.some(
      (paneInput) => paneInput.id !== paneInputId && paneInput.name === name
    )
  ) {
    name = `${name}_`;
    if (name.length > 16) {
      return undefined;
    }
  }
  return name;
}

export function getPaneInputAutoName(
  existingPaneInputs: PaneInput[],
  inputPaneOutputIndex: number | undefined,
  inputPane: Pane | undefined,
  paneInputId: string | undefined
): string {
  let inputPaneName: string | undefined = (inputPane as any)?.name?.replace(
    / /g,
    "_"
  );
  if (inputPaneOutputIndex === 1 || inputPaneOutputIndex === 2) {
    if (inputPane?.type === PaneType.State) {
      const prefix = inputPaneOutputIndex === 2 ? "get" : "set";
      if (inputPaneName === undefined) {
        inputPaneName = `${prefix}Value`;
      } else {
        inputPaneName = `${prefix}${inputPaneName[0].toUpperCase()}${inputPaneName.slice(
          1
        )}`;
      }
    } else {
      inputPaneName = undefined;
    }
  }
  if (
    inputPaneName === undefined &&
    inputPane?.type === PaneType.EnvironmentVariable
  ) {
    inputPaneName = inputPane.key;
  }
  let fallbackName: string | undefined;
  if (paneInputId !== undefined) {
    const existingPaneInputName = existingPaneInputs.find(
      (input) => input.id === paneInputId
    )?.name;
    fallbackName = existingPaneInputName;
  }
  if (fallbackName === undefined) {
    const existingValueSuffixes: number[] = [];
    existingPaneInputs.forEach((paneInput) => {
      if (paneInput.name !== undefined) {
        const matches = /^value(\d*)$/.exec(paneInput.name);
        if (matches !== null) {
          if (matches[1] === "") {
            existingValueSuffixes.push(1);
          } else {
            existingValueSuffixes.push(parseInt(matches[1]));
          }
        }
      }
    });
    fallbackName = `value${
      existingValueSuffixes.length > 0
        ? Math.max(...existingValueSuffixes) + 1
        : ""
    }`;
  }
  if (typeof inputPaneName !== "string" || inputPaneName.length === 0) {
    return fallbackName;
  }
  if (!VALID_VARIABLE_NAME_REGEX.test(inputPaneName)) {
    return fallbackName;
  }
  return (
    validatePaneInputName(inputPaneName, existingPaneInputs, paneInputId) ??
    fallbackName
  );
}

export function getRectFromLayoutPane(layoutPane: CanvasLayoutPane): Rect {
  if (layoutPane.render?.[0] === CanvasLayoutPaneRenderType.RunButton) {
    return [
      layoutPane.rect[0],
      layoutPane.rect[1],
      RENDER_RUN_BUTTON_WIDTH,
      RENDER_RUN_BUTTON_HEIGHT,
    ];
  }
  return layoutPane.rect;
}

export function getAddLayoutPaneRect(paneId: string): Rect {
  const pane = canvasAtom.value.panes.find((p) => p.id === paneId)!;
  return [
    ...roundPositionToGrid(
      [canvasViewport.offset[0] + 16, canvasViewport.offset[1] + 16],
      1,
      Math.ceil
    ),
    pane.rect[2],
    pane.rect[3],
  ];
}

export function movePaneToTopIfNeeded(paneId: string) {
  runInAction(() => {
    const zIndex = activePaneStates.get(paneId)?.zIndex.get();
    const rect = activePaneStates.get(paneId)?.rect.get();
    if (zIndex === undefined || rect === undefined) {
      return;
    }
    const hasOverlappingPaneAbove = entries(activePaneStates).some(
      ([pId, activePaneState]) => {
        if (pId === paneId) {
          return false;
        }
        return (
          activePaneState.zIndex.get() > zIndex &&
          doRectsOverlap(activePaneState.rect.get(), rect)
        );
      }
    );
    if (!hasOverlappingPaneAbove) {
      return;
    }
    const maxZIndex = Math.max(
      ...values(activePaneStates).map((activePaneState) =>
        activePaneState.zIndex.get()
      )
    );
    paneMutator.updateZ(paneId, maxZIndex + 1);
  });
}

export function isPaneCollapsed(pane: { flags?: number } | undefined) {
  return pane !== undefined && hasFlag(pane.flags ?? 0, PaneFlag.Collapse);
}

const DISCRETE_ZOOM_LEVELS = [0.25, 0.5, 1, 2];
export function getZoomWithDirection(
  prevZoom: number,
  direction: "in" | "out"
): number | undefined {
  if (direction === "in") {
    const nextZoom = DISCRETE_ZOOM_LEVELS.find((zoom) => zoom > prevZoom);
    if (nextZoom !== undefined) {
      return nextZoom;
    }
  } else {
    const nextZoom = [...DISCRETE_ZOOM_LEVELS]
      .reverse()
      .find((zoom) => zoom < prevZoom);
    if (nextZoom !== undefined) {
      return nextZoom;
    }
  }
}

export function zoomToLevel(newZoom: number, viewportTarget?: Position) {
  const prevZoom = canvasViewport.zoom;
  if (newZoom === prevZoom) {
    return;
  }
  runInAction(() => {
    canvasViewportShouldAnimateAtom.value = true;
    canvasViewport.offset = calculateNewZoomCanvasOffset(
      canvasViewport.offset,
      viewportTarget ?? [
        window.innerWidth / 2,
        (window.innerHeight + headerHeightComputed.get()) / 2,
      ],
      prevZoom,
      newZoom
    );
    canvasViewport.zoom = newZoom;
  });
}

export function getRectForCollapsedPane(
  paneRect: Rect,
  paneType: PaneType,
  paneName: string | undefined
): Rect {
  return [
    paneRect[0],
    paneRect[1],
    Math.ceil(
      (getWidthForMonospaceText(paneName ?? "name") +
        (paneType === PaneType.State ? 128 : 80)) /
        GRID_UNIT
    ) * GRID_UNIT,
    paneType === PaneType.State ? 48 : 32,
  ];
}

export function setSelectedToolToPointer() {
  if (selectedToolAtom.value[0] !== "pointer") {
    runInAction(() => {
      selectedToolAtom.value = ["pointer"];
    });
  }
}

export function reactionOnCondition(
  condition: () => boolean,
  effect: () => () => void
): () => void {
  let cleanup: (() => void) | undefined;
  const unsubscribe = reaction(
    condition,
    (active) => {
      if (active) {
        cleanup = effect();
      } else {
        cleanup?.();
        cleanup = undefined;
      }
    },
    { fireImmediately: true }
  );
  return () => {
    unsubscribe();
    cleanup?.();
  };
}

export function getTopPaneAtPosition(
  panes: Pane[],
  xy: Position
): Pane | undefined {
  for (const p of [...panes].sort((a, b) => b.z - a.z)) {
    const activePaneState = activePaneStates.get(p.id);
    if (
      activePaneState === undefined ||
      activePaneState.viewMode.get() === PaneViewMode.Hidden
    ) {
      return;
    }
    const paneRect = activePaneState.rect.get();
    if (
      p.type !== PaneType.EvaluateTemplate &&
      isPointInsideRect(xy, paneRect)
    ) {
      return p;
    }
  }
}

export function getPaneOutputWirePosition(
  paneRect: Rect,
  outputIndex: number
): Position {
  const [x1, y1, w1] = paneRect;
  return [x1 + w1 + 1, y1 + 16.5 + outputIndex * 16];
}

export function getPaneInputsWithSource(
  panes: Pane[],
  source: [paneId: string, outputIndex: number]
) {
  const [paneId, outputIndex] = source;
  const rv: PaneInputGlobalKey[] = [];
  panes.forEach((p) => {
    if (p.type === PaneType.Evaluate) {
      p.inputs.forEach((input) => {
        if (input.source?.[0] === paneId && input.source[1] === outputIndex) {
          rv.push([PaneType.Evaluate, p.id, input.id]);
        }
      });
      if (
        p.renderOutput?.[0] === "custom" &&
        p.renderOutput[1]?.[0] === paneId &&
        p.renderOutput[1][1] === outputIndex
      ) {
        rv.push([PaneType.Evaluate, p.id, ["render", "custom"]]);
      }
      if (
        p.renderOutput?.[0] === "react" &&
        p.renderOutput[1]?.ReactDOM?.[0] === paneId &&
        p.renderOutput[1].ReactDOM[1] === outputIndex
      ) {
        rv.push([PaneType.Evaluate, p.id, ["render", "react-dom"]]);
      }
    }
    if (p.type === PaneType.EvaluateTemplate) {
      Object.entries(p.templateInputSources).forEach(
        ([inputId, inputSource]) => {
          if (inputSource[0] === paneId && inputSource[1] === outputIndex) {
            rv.push([PaneType.EvaluateTemplate, p.id, inputId]);
          }
        }
      );
    }
  });
  return rv;
}

export function areEvalPaneInputIdsEqual(
  a: EvalPaneInputId,
  b: EvalPaneInputId
) {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a[0] === b[0] && a[1] === b[1];
  }
  if (typeof a === "string" && typeof b === "string") {
    return a === b;
  }
  return false;
}

export function updatePaneInputWithSource(
  paneInputGlobalKey: PaneInputGlobalKey,
  inputSource: [paneId: string, outputIndex: number]
) {
  const [paneType, paneId, inputId] = paneInputGlobalKey;
  if (paneType === PaneType.Evaluate) {
    if (Array.isArray(inputId)) {
      if (inputId[0] === "render") {
        canvasDispatch([
          ActionType.SetPaneRenderOutput,
          {
            paneId,
            renderOutput:
              inputId[1] === "custom"
                ? ["custom", inputSource]
                : inputId[1] === "react-dom"
                ? ["react", { ReactDOM: inputSource }]
                : undefined,
          },
        ]);
      }
    } else {
      canvasDispatch([
        ActionType.SetPaneInputSource,
        {
          paneId,
          paneInputId: inputId,
          source: inputSource,
        },
      ]);
    }
  } else if (paneType === PaneType.EvaluateTemplate) {
    canvasDispatch([
      ActionType.SetPaneTemplateInputSource,
      {
        paneId,
        paneInputId: inputId,
        source: inputSource,
      },
    ]);
  } else if (paneType === PaneType.LLM) {
    canvasDispatch([
      ActionType.SetPaneLLMInputSource,
      {
        paneId,
        source: inputSource,
      },
    ]);
  }
}

export function arePanesPartOfFunctionPane(paneIds: string[]) {
  return canvasAtom.value.panes.some(
    (p) =>
      p.type === PaneType.Function &&
      p.paneIds.some((id) => paneIds.includes(id))
  );
}

const LLM_CHAT_MODELS = ["gpt-3.5-turbo"];
export function isLLMConfigChat(llmConfig: LLMConfig) {
  return LLM_CHAT_MODELS.includes(llmConfig.model);
}
